import { useEffect } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { AlertCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useMyCart } from "../../contexts/MyCartContext";

// Height of AddOnToast (approx 80px content + 3px bar) + gap
const ADDON_TOAST_OFFSET = "5.5rem";

const RED = "#DC2626";

export default function PhoneVerifyToast() {
  const { phoneWarningToast, toast: addOnToast } = useMyCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPostPaymentSuccessRoute =
    pathname === "/book/success" || pathname === "/packages/return";

  // Dismiss when user is already on the account page
  useEffect(() => {
    if (pathname === "/account" && phoneWarningToast.visible) {
      phoneWarningToast.dismiss();
    }
  }, [pathname, phoneWarningToast]);

  return (
    <AnimatePresence>
      {phoneWarningToast.visible && pathname !== "/account" && !isPostPaymentSuccessRoute && (
        <div
          style={{
            position: "fixed",
            top: addOnToast.visible && pathname !== "/my-cart" ? ADDON_TOAST_OFFSET : "1rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9998,
            width: "calc(100% - 2rem)",
            maxWidth: "26rem",
            pointerEvents: "none",
          }}
        >
          <motion.div
            key="phone-verify-toast"
            initial={{ opacity: 0, y: -72 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -56, transition: { duration: 0.18, ease: "easeIn" } }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            style={{ pointerEvents: "auto" }}
          >
            <Box
              bg="rgba(255,255,255,0.88)"
              style={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
              rounded="0.85rem"
              boxShadow="0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)"
              overflow="hidden"
              border="1px solid rgba(220,38,38,0.28)"
              cursor="pointer"
              onClick={() => {
                phoneWarningToast.dismiss();
                navigate("/account");
              }}
            >
              {/* Main row */}
              <Flex align="center" gap="0.75rem" px="0.85rem" py="0.75rem">
                {/* Amber icon square */}
                <Flex
                  w="2.2rem"
                  h="2.2rem"
                  rounded="0.5rem"
                  bg={RED}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <AlertCircle size={15} color="white" />
                </Flex>

                {/* Text */}
                <Box flex="1" minW={0}>
                  <Text fontSize="0.83rem" fontWeight="700" color="#1e293b" lineHeight="1.3">
                    Booking confirmation not delivered
                  </Text>
                  <Text fontSize="0.72rem" color="#64748b" lineHeight="1.4" mt="0.06rem">
                    Tap to verify your contact number
                  </Text>
                </Box>

                {/* Dismiss */}
                <Box
                  w="1.5rem"
                  h="1.5rem"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="#94a3b8"
                  cursor="pointer"
                  rounded="full"
                  flexShrink={0}
                  _hover={{ color: "#475569", bg: "#f1f5f9" }}
                  transition="all 0.15s"
                  onClick={(e) => {
                    e.stopPropagation();
                    phoneWarningToast.dismiss();
                  }}
                >
                  <X size={13} />
                </Box>
              </Flex>

              {/* Amber accent bar at bottom */}
              <Box h="3px" bg={RED} opacity={0.35} />
            </Box>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
